import { useCallback, useMemo, useState } from 'react';

import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';

import { settings, useSetting } from '../../../../../../../../settings';
import { DevExpressReportOptConf } from '../../../../../../../../settings/settings';

interface UseReportValuesProps {
  report: Report2;
  bzObjectType: BzObjType;
}

export const useDevExpressReportValues = (props: UseReportValuesProps) => {
  const { report, bzObjectType } = props;

  const devExpressReportOptGlobalConf = useSetting(
    settings.devExpressReportOptGlobalConf()
  );

  const globalReportOptSettings = useMemo<DevExpressReportOptConf>(() => {
    const persistedBzObjSettings: DevExpressReportOptConf[] =
      devExpressReportOptGlobalConf?.value?.[bzObjectType];

    const persistedReportOptSettings = persistedBzObjSettings?.find(
      (x) => x.reportId === report?.id
    );

    if (persistedReportOptSettings) {
      return persistedReportOptSettings;
    }

    return {
      reportId: report?.id,
      options: [],
    };
  }, [bzObjectType, devExpressReportOptGlobalConf?.value, report?.id]);

  const getDefaultValues = useCallback(() => {
    const returnValue: Record<string, string> = {};
    for (const opt of globalReportOptSettings.options) {
      if (!report.options?.find((x) => x.name === opt.name)) return;
      returnValue[opt.name] = opt?.defaultValue;
    }

    return returnValue;
  }, [globalReportOptSettings.options, report?.options]);

  // use it for checkbox management
  const [values, setValues] =
    useState<Record<string, string>>(getDefaultValues);

  const init = useCallback(() => {
    if (!report) return;
    const returnValue = getDefaultValues();
    setValues((values) => {
      if (JSON.stringify(values) === JSON.stringify(returnValue)) return values;
      return returnValue;
    });
  }, [getDefaultValues, report]);

  const handleOnChange = useCallback(
    (optId: string, value: string) => {
      setValues((state) => ({ ...state, [optId]: value }));
    },
    [setValues]
  );

  return useMemo(
    () => ({
      globalReportOptSettings,
      values: values,
      onChange: handleOnChange,
      init,
    }),
    [globalReportOptSettings, handleOnChange, values, init]
  );
};
